/**
 * Navigation configuration for MUI Toolpad AppProvider
 * Based on the VRP system architecture and Supabase design specifications
 * 
 * Structure:
 * - Dashboard (Landing page)
 * - Projects (Hierarchical view - triggers secondary sidebar)
 * - Table Editor (Flat table access - triggers secondary sidebar)
 * - Settings (Configuration)
 * - Tools (Utilities)
 */

import React from 'react';
import {
  Dashboard as DashboardIcon,
  Folder as ProjectsIcon,
  TableChart as TableEditorIcon,
  Settings as SettingsIcon,
  Build as ToolsIcon,
} from '@mui/icons-material';

export interface NavigationItem {
  segment: string;
  title: string;
  pattern?: string;
  children?: NavigationItem[];
  kind?: 'page' | 'header' | 'divider';
  icon?: React.ReactElement;
}

/**
 * Main navigation structure for Toolpad AppProvider
 */
export const NAVIGATION: NavigationItem[] = [
  // Dashboard - Landing page
  {
    segment: 'dashboard',
    title: 'Dashboard',
    kind: 'page',
    icon: <DashboardIcon sx={{ fontSize: 16 }} />,
  },
  
  // Divider before domain switcher
  {
    kind: 'divider',
    segment: 'domain-divider',
    title: '',
  },
  
  // Projects - Triggers secondary sidebar to show project hierarchy
  {
    segment: 'projects',
    title: 'Projects',
    kind: 'page',
    icon: <ProjectsIcon sx={{ fontSize: 16 }} />,
    // No children - hierarchy will be shown in secondary sidebar
  },

  // Table Editor - Triggers secondary sidebar to show table list
  {
    segment: 'table-editor',
    title: 'Table Editor',
    kind: 'page',
    icon: <TableEditorIcon sx={{ fontSize: 16 }} />,
    // No children - table list will be shown in secondary sidebar
  },


  // Settings - Configuration and preferences
  {
    segment: 'settings',
    title: 'Settings',
    kind: 'page',
    icon: <SettingsIcon sx={{ fontSize: 16 }} />,
    // No children - flat navigation item
  },

  // Tools - Utilities and helpers
  {
    segment: 'tools',
    title: 'Tools',
    kind: 'page',
    icon: <ToolsIcon sx={{ fontSize: 16 }} />,
    // No children - flat navigation item
  },
];

/**
 * Branding configuration for Toolpad AppProvider
 */
export const BRANDING = {
  title: 'VRP System',
  homeUrl: '/dashboard',
  // Logo will be added in Phase 2 with DECOY branding
};

/**
 * Helper function to get navigation item by segment
 */
export function getNavigationItem(segment: string): NavigationItem | undefined {
  function findItem(items: NavigationItem[], targetSegment: string): NavigationItem | undefined {
    for (const item of items) {
      if (item.segment === targetSegment) {
        return item;
      }
      if (item.children) {
        const found = findItem(item.children, targetSegment);
        if (found) return found;
      }
    }
    return undefined;
  }
  
  return findItem(NAVIGATION, segment);
}

/**
 * Helper function to build breadcrumb path from current route
 */
export function buildBreadcrumbPath(pathname: string): NavigationItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: NavigationItem[] = [];
  
  let currentItems = NAVIGATION;
  
  for (const segment of segments) {
    const item = currentItems.find(item => 
      item.segment === segment || 
      item.pattern?.includes(`:${segment}`)
    );
    
    if (item) {
      breadcrumbs.push(item);
      currentItems = item.children || [];
    }
  }
  
  return breadcrumbs;
}

/**
 * Helper function to check if a route is active
 */
export function isRouteActive(itemSegment: string, currentPathname: string): boolean {
  const segments = currentPathname.split('/').filter(Boolean);
  return segments.includes(itemSegment);
}

/**
 * Navigation configuration for secondary sidebar content
 * Used by Smart Secondary Sidebar component
 */
export const SECONDARY_NAVIGATION = {
  projects: {
    title: 'Project Hierarchy',
    description: 'Navigate through projects, scenarios, and datasets',
    searchPlaceholder: 'Search projects...',
  },
  'table-editor': {
    title: 'Table Editor',
    description: 'Access all tables across projects',
    searchPlaceholder: 'Search tables...',
  },
  settings: {
    title: 'Settings & Configuration',
    description: 'Manage system preferences',
    searchPlaceholder: 'Search settings...',
  },
  tools: {
    title: 'Data Tools',
    description: 'Import, export, and validate data',
    searchPlaceholder: 'Search tools...',
  },
};

export default NAVIGATION;